// events.js
// Firebase events collection API functions
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { uploadImage, deleteImage, updateImage } from '../utils/imageUpload';

const EVENTS_COLLECTION = 'events';

/**
 * Get all events
 */
export const getAllEvents = async () => {
  try {
    const eventsRef = collection(db, EVENTS_COLLECTION);
    const q = query(eventsRef, orderBy('date', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const events = [];
    querySnapshot.forEach((doc) => {
      events.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    return events;
  } catch (error) {
    console.error('Error getting events:', error);
    throw error;
  }
};

/**
 * Get event by ID
 */
export const getEventById = async (eventId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const eventSnap = await getDoc(eventRef);
    
    if (eventSnap.exists()) {
      return {
        id: eventSnap.id,
        ...eventSnap.data(),
      };
    } else {
      throw new Error('Event not found');
    }
  } catch (error) {
    console.error('Error getting event:', error);
    throw error;
  }
};

/**
 * Create new event (admin only)
 * @param {Object} eventData - Event data including image file
 * @param {File} imageFile - Optional image file to upload
 */
export const createEvent = async (eventData, imageFile = null) => {
  try {
    let imageUrl = null;
    
    // Upload image if provided
    if (imageFile) {
      imageUrl = await uploadImage(imageFile, 'events');
    }
    
    const eventsRef = collection(db, EVENTS_COLLECTION);
    const docRef = await addDoc(eventsRef, {
      ...eventData,
      imageUrl,
      participants: [],
      volunteers: [],
      participationEnabled: eventData.participationEnabled !== undefined ? eventData.participationEnabled : true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

/**
 * Update event (admin only)
 * @param {string} eventId - The event ID
 * @param {Object} eventData - Updated event data
 * @param {File} imageFile - Optional new image file to upload
 */
export const updateEvent = async (eventId, eventData, imageFile = null) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    
    let updateData = { ...eventData };
    
    // Handle image update if new file provided
    if (imageFile) {
      // Get current event to retrieve old image URL
      const eventSnap = await getDoc(eventRef);
      const oldImageUrl = eventSnap.exists() ? eventSnap.data().imageUrl : null;
      
      // Upload new image and delete old one
      const newImageUrl = await updateImage(imageFile, oldImageUrl, 'events');
      updateData.imageUrl = newImageUrl;
    }
    
    await updateDoc(eventRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

/**
 * Delete event (admin only)
 */
export const deleteEvent = async (eventId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    
    // Get event data to retrieve image URL
    const eventSnap = await getDoc(eventRef);
    if (eventSnap.exists()) {
      const imageUrl = eventSnap.data().imageUrl;
      
      // Delete image if exists
      if (imageUrl) {
        await deleteImage(imageUrl);
      }
    }
    
    // Delete event document
    await deleteDoc(eventRef);
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

/**
 * Register user as participant for an event
 */
export const participateInEvent = async (eventId, userId, userName) => {
  try {
    // Check if participation is enabled
    const event = await getEventById(eventId);
    if (event.participationEnabled === false) {
      throw new Error('Participation is currently disabled for this event');
    }
    
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(eventRef, {
      participants: arrayUnion({
        userId,
        userName,
        registeredAt: new Date().toISOString(),
      }),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error participating in event:', error);
    throw error;
  }
};

/**
 * Unregister user from event
 */
export const unregisterFromEvent = async (eventId, userId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const eventSnap = await getDoc(eventRef);
    
    if (eventSnap.exists()) {
      const event = eventSnap.data();
      const updatedParticipants = event.participants.filter(
        (participant) => participant.userId !== userId
      );
      
      await updateDoc(eventRef, {
        participants: updatedParticipants,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error unregistering from event:', error);
    throw error;
  }
};

/**
 * Check if user is registered for an event
 */
export const isUserParticipating = async (eventId, userId) => {
  try {
    const event = await getEventById(eventId);
    return event.participants?.some(
      (participant) => participant.userId === userId
    ) || false;
  } catch (error) {
    console.error('Error checking participation:', error);
    return false;
  }
};

/**
 * Toggle event participation status (admin only)
 * @param {string} eventId - The event ID
 * @param {boolean} enabled - Whether to enable or disable participation
 */
export const toggleEventParticipation = async (eventId, enabled) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(eventRef, {
      participationEnabled: enabled,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error toggling event participation:', error);
    throw error;
  }
};

/**
 * Remove a participant from an event (admin only)
 * @param {string} eventId - The event ID
 * @param {string} userId - The user ID to remove
 */
export const removeParticipant = async (eventId, userId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const eventSnap = await getDoc(eventRef);
    
    if (eventSnap.exists()) {
      const event = eventSnap.data();
      const updatedParticipants = event.participants.filter(
        (participant) => participant.userId !== userId
      );
      
      await updateDoc(eventRef, {
        participants: updatedParticipants,
        updatedAt: serverTimestamp(),
      });
    } else {
      throw new Error('Event not found');
    }
  } catch (error) {
    console.error('Error removing participant:', error);
    throw error;
  }
};
